/**
 * The Needs-you band on the tenant home, drawn as the Overview board draws
 * its rows: an icon in the severity's tint, the title, a detail line, and
 * the action on the row. An empty band says so rather than vanishing, so a
 * quiet tenant reads as quiet, not as unloaded.
 */
import { useNavigate } from 'react-router-dom';

import { useRecordPanel } from '../../components/shell/record.js';
import { Button } from '../../ui/Button.js';
import { Icon } from '../../ui/finixt.js';
import type { NeedAction, NeedSeverity, TenantNeed } from './tenant-needs.js';

const TONE: Record<NeedSeverity, { background: string; color: string }> = {
  broken: { background: 'var(--danger-tint)', color: 'var(--danger)' },
  waiting: { background: 'var(--warn-tint)', color: 'var(--warn)' },
};

export function TenantNeeds({ rows, loading }: { rows: TenantNeed[]; loading: boolean }) {
  if (rows.length === 0) {
    return (
      <div
        style={{
          borderRadius: 'var(--radius-card)',
          border: '1px solid var(--border-hairline)',
          background: 'var(--surface-card)',
          padding: 22,
          textAlign: 'center',
        }}
      >
        <Icon
          name={loading ? 'hourglass_empty' : 'check_circle'}
          size={22}
          color={loading ? 'var(--text-ghost)' : 'var(--ok)'}
        />
        <p style={{ marginTop: 7, fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
          {loading ? 'Checking…' : 'This tenant needs nothing right now.'}
        </p>
      </div>
    );
  }
  return (
    <div
      style={{
        overflow: 'hidden',
        borderRadius: 'var(--radius-card)',
        border: '1px solid var(--border-hairline)',
        background: 'var(--surface-card)',
      }}
    >
      {rows.map((row, index) => (
        <NeedRow key={row.id} row={row} first={index === 0} />
      ))}
    </div>
  );
}

function NeedRow({ row, first }: { row: TenantNeed; first: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        padding: '11px 16px',
        borderTop: first ? 'none' : '1px solid var(--border-hairline)',
      }}
    >
      <span
        style={{
          display: 'grid',
          width: 32,
          height: 32,
          flexShrink: 0,
          placeItems: 'center',
          borderRadius: 'var(--radius-input)',
          ...TONE[row.severity],
        }}
      >
        <Icon name={row.icon} size={16} />
      </span>
      <span style={{ minWidth: 0, flex: '1 1 0%' }}>
        <span
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 'var(--text-body-md)',
            fontWeight: 'var(--weight-medium)',
            color: 'var(--text-strong)',
          }}
        >
          {row.title}
        </span>
        <span
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 'var(--text-meta-lg)',
            color: 'var(--text-muted)',
          }}
        >
          {row.detail}
        </span>
      </span>
      <NeedButton action={row.action} />
    </div>
  );
}

function NeedButton({ action }: { action: NeedAction }) {
  const navigate = useNavigate();
  const panel = useRecordPanel();
  function run() {
    if (action.kind === 'navigate') navigate(action.to);
    else if (action.kind === 'record') panel.open(action.ref);
    else
      document
        .getElementById(action.target)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  return (
    <Button size="sm" variant="secondary" onClick={run}>
      {action.label}
    </Button>
  );
}
